<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <Table
      v-model:selected="selected"
      v-model:sort="sort"
      class="flex min-h-0 flex-1 flex-col"
    >
      <Table.Head>
        <Table.Header primary field="name" sortable>Name</Table.Header>
        <Table.Header field="timestamp" sortable class="w-[270px]">
          Dacr Timestamp
        </Table.Header>
        <Table.Header class="w-[190px]">Status</Table.Header>
      </Table.Head>

      <template v-if="loading && !rows.length">
        <Table.Row v-for="n in 7" :key="`skeleton-${n}`" :id="`skeleton-${n}`">
          <template #media>
            <div class="flex w-9 items-center justify-center tablet-h:w-6">
              <Skeleton class="h-6 w-[17px]" />
            </div>
          </template>

          <Table.Cell primary>
            <Skeleton class="h-[13px] w-[120px]" />
          </Table.Cell>
          <Table.Cell class="w-[270px]">
            <Skeleton class="h-[13px] w-[120px]" />
          </Table.Cell>
          <Table.Cell class="w-[190px]">
            <Skeleton class="h-[13px] w-[60px]" />
          </Table.Cell>

          <template #mobile>
            <div class="flex w-full items-center pl-[10px] pr-[10px]">
              <div class="min-w-0 flex-1">
                <Skeleton class="mb-1 h-[13px] w-[120px]" />
                <Skeleton class="h-[13px] w-[150px]" />
              </div>
            </div>
          </template>
        </Table.Row>
      </template>

      <div v-else-if="!rows.length" class="min-h-0 flex-1">
        <div
          class="flex flex-col items-center justify-center px-6 pt-24 text-center"
        >
          <div class="text-[16px] font-medium leading-[20px] text-gray-800">
            No results found
          </div>
          <div class="mt-1.5 text-[14px] leading-[20px] text-gray-500">
            Try different keywords
          </div>
        </div>
      </div>

      <Table.Virtual
        v-else
        :rows="rows"
        :row-height="rowHeight"
        class="min-h-0 flex-1 pb-[calc(env(safe-area-inset-bottom)+100px)] tablet-h:pb-0"
        @row-click="
          (_event: MouseEvent, item: unknown) => onRowClick(item as Item)
        "
        @scroll-end="emit('scroll-end')"
      >
        <template #media-row="{ row }: { row: Item }">
          <div class="w-9 tablet-h:w-6" :class="itemDimClass(row)">
            <Thumbnail :type="thumbType(row)" :src="thumbSrc(row)" />
          </div>
        </template>

        <template #row="{ row }: { row: Item }">
          <Table.Cell primary :class="itemDimClass(row)">
            <span class="truncate">{{ row.name }}</span>
          </Table.Cell>

          <Table.Cell class="w-[270px]" :class="itemDimClass(row)">
            <template v-if="timestampLabel(row)">
              <DacrMarkIcon class="mr-2 size-[18px] shrink-0 text-gray-800" />
              <span class="truncate">{{ timestampLabel(row) }}</span>
            </template>
            <span v-else class="opacity-50">—</span>
          </Table.Cell>

          <Table.Cell class="w-[190px]" :class="itemDimClass(row)">
            {{ statusLabel(row) }}
          </Table.Cell>
        </template>

        <template #mobile-row="{ row }: { row: Item }">
          <div
            class="flex w-full items-center pl-[10px] pr-[10px]"
            :class="itemDimClass(row)"
          >
            <div class="min-w-0 flex-1">
              <p class="truncate text-[14px] leading-[18px] text-gray-800">
                {{ row.name }}
              </p>
              <div
                class="mt-[2px] flex items-center gap-[6px] text-[13px] leading-[18px] text-[#5a616b]"
              >
                <template v-if="timestampLabel(row)">
                  <DacrMarkIcon class="size-[14px] shrink-0 text-gray-800" />
                  <span class="truncate">{{ timestampLabel(row) }}</span>
                </template>
                <span v-else class="opacity-50">—</span>
              </div>
            </div>
          </div>
        </template>
      </Table.Virtual>
    </Table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Table, Thumbnail, Skeleton, type TableSort } from '@dacr/library'
import { DacrMarkIcon } from '@dacr/icons'
import {
  FileStatus,
  ItemType,
  type Item,
} from '@/shared/graphql/legacy/generated'
import {
  itemThumbType,
  itemThumbSrc,
  itemDimClass,
  isItemInactive,
} from '@/features/search/utils'
import { formatTimestamp } from '@/shared/utils/datetime'
import { useDevice } from '@/shared/composables/useDevice'

const props = defineProps<{
  items?: Item[]
  loading?: boolean
}>()

const emit = defineEmits<{ 'scroll-end': [] }>()

const selected = defineModel<string[]>('selected', { default: () => [] })
const sort = defineModel<TableSort | null>('sort', { default: null })

const device = useDevice()
const router = useRouter()

const rows = computed(() => props.items ?? [])

const rowHeight = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value ? 42 : 65
)

const thumbType = (item: Item) => itemThumbType(item)
const thumbSrc = (item: Item) => itemThumbSrc(item) ?? undefined

const timestampLabel = (item: Item) =>
  item.type === ItemType.Folder
    ? ''
    : formatTimestamp(item.file?.timestamp ?? item.timestamp)

const statusLabel = (item: Item) =>
  item.file?.status === FileStatus.Inactive ? 'Inactive' : '—'

const onRowClick = (item: Item) => {
  if (device.isMobile.value || device.isTabletVertical.value) {
    if (isItemInactive(item)) return
    if (item.type === ItemType.Folder) {
      router.push(`/home${item.parentPath}/${item.name}`.replace('//', '/'))
    } else if (item.file) {
      router.push(`/file/${item.file.copyrightId}`)
    }
    return
  }
  selected.value = selected.value[0] === item.id ? [] : [item.id]
}
</script>
